import React, { Suspense, lazy } from 'react';
import { createBrowserRouter } from "react-router-dom";
import { Box, Skeleton } from '@mui/material';
import { ProtectedRoute } from "./components/ProtectedRoute";
import { PublicRoute } from "./components/PublicRoute";

// Lazy load all page components for code splitting
const Home = lazy(() => import("./pages/home/home"));
const Signup = lazy(() => import("./pages/signup/signup"));
const ForgotPassword = lazy(() => import("./pages/forgot_password/forgot_password"));
const ResetPassword = lazy(() => import("./pages/reset_password/reset_password"));
const VerifyEmail = lazy(() => import("./pages/verify_email/verify_email"));
const AuthCallback = lazy(() => import("./pages/auth_sso/auth_callback"));
const Dashboard = lazy(() => import("./pages/dashboard/dashboard"));
const Clusters = lazy(() => import("./pages/clusters/clusters"));
const DsrcOverview = lazy(() => import("./pages/datasources/datasource"));
const DataFiles = lazy(() => import("./pages/data_files/data_files"));
const AllFiles = lazy(() => import("./pages/all_files/all_files"));
const ClusterDebugInfoPage = lazy(() => import("./pages/cluster_debug/cluster_debug_info_page"));
const Crashlog = lazy(() => import("./pages/crashlog/crashlog"));
const DbQueryRunner = lazy(() => import("./pages/db_query/db_query_runner"));
const Notifications = lazy(() => import("./pages/notifications/notifications"));
const PerformanceThresholds = lazy(() => import("./pages/notifications/thresholds"));
const PerformanceAlerts = lazy(() => import("./pages/performance_alerts/performance_alerts"));
const NotificationSettings = lazy(() => import("./pages/notification_settings/notification_settings"));
const InvestigateNotification = lazy(() => import("./pages/investigate/InvestigateNotification"));
const NotFound = lazy(() => import("./pages/notfound/NotFound"));

// Loading fallback — skeleton content preview
const PageLoader = () => (
    <Box sx={{ p: 3 }}>
        {/* Title area */}
        <Skeleton animation="wave" width={220} height={28} sx={{ borderRadius: 1, mb: 0.5 }} />
        <Skeleton animation="wave" width={160} height={14} sx={{ borderRadius: 1, mb: 3 }} />
        {/* Stats bar */}
        <Skeleton animation="wave" variant="rectangular" height={80} sx={{ borderRadius: 2, mb: 3 }} />
        {/* Tab bar */}
        <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
            <Skeleton animation="wave" width={90} height={32} sx={{ borderRadius: 1.5 }} />
            <Skeleton animation="wave" width={60} height={32} sx={{ borderRadius: 1.5 }} />
            <Skeleton animation="wave" width={50} height={32} sx={{ borderRadius: 1.5 }} />
        </Box>
        {/* Table skeleton */}
        <Box sx={{ border: '1px solid #E5E7EB', borderRadius: 2, overflow: 'hidden', backgroundColor: '#fff' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, px: 2, py: 1.5, backgroundColor: '#FAFBFC', borderBottom: '1px solid #E5E7EB' }}>
                <Skeleton animation="wave" width={80} height={12} sx={{ borderRadius: 1 }} />
                <Box sx={{ flex: 1 }} />
                {[50, 50, 55, 60, 60, 55, 50, 55, 45].map((w, i) => (
                    <Skeleton key={i} animation="wave" width={w} height={12} sx={{ borderRadius: 1, flexShrink: 0 }} />
                ))}
            </Box>
            {[160, 120, 180, 105, 145, 135].map((nameW, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 3, px: 2, py: 2, borderBottom: '1px solid #F3F4F6' }}>
                    <Skeleton animation="wave" variant="circular" width={8} height={8} sx={{ flexShrink: 0 }} />
                    <Box sx={{ minWidth: 160 }}>
                        <Skeleton animation="wave" width={nameW} height={16} sx={{ borderRadius: 1 }} />
                        <Skeleton animation="wave" width={140} height={10} sx={{ mt: 0.5, borderRadius: 1 }} />
                    </Box>
                    <Box sx={{ flex: 1 }} />
                    {[50, 35, 45, 55, 65, 60, 50, 50, 40].map((w, j) => (
                        <Skeleton key={j} animation="wave" width={w} height={14} sx={{ borderRadius: 1, flexShrink: 0 }} />
                    ))}
                </Box>
            ))}
        </Box>
    </Box>
);

// Wrap component with Suspense
const withSuspense = (Component) => (
    <Suspense fallback={<PageLoader />}>
        {Component}
    </Suspense>
);

const router = createBrowserRouter([
    {
        path: "/",
        element: <PublicRoute>{withSuspense(<Home />)}</PublicRoute>,
    },
    {
        path: "/signup",
        element: <PublicRoute>{withSuspense(<Signup />)}</PublicRoute>,
    },
    {
        path: "/forgot-password",
        element: <PublicRoute>{withSuspense(<ForgotPassword />)}</PublicRoute>
    },
    {
        path: "/reset_password/:token",
        element: <PublicRoute>{withSuspense(<ResetPassword />)}</PublicRoute>
    },
    {
        path: "/dashboard",
        element: <ProtectedRoute>{withSuspense(<Dashboard />)}</ProtectedRoute>,
    },
    {
        path: "/dashboard/:clusterName/datasources/overview",
        element: <ProtectedRoute>{withSuspense(<DsrcOverview />)}</ProtectedRoute>,
    },
    {
        path: "/clusters",
        element: <ProtectedRoute>{withSuspense(<Clusters />)}</ProtectedRoute>
    },
    {
        path: "/files/:clusterName/:tableName",
        element: <ProtectedRoute>{withSuspense(<DataFiles />)}</ProtectedRoute>
    },
    {
        path: "/allfiles/:clusterName",
        element: <ProtectedRoute>{withSuspense(<AllFiles />)}</ProtectedRoute>
    },
    {
        path: "/cluster_debug_info/:clusterName",
        element: <ProtectedRoute>{withSuspense(<ClusterDebugInfoPage />)}</ProtectedRoute>
    },
    {
        path: "/crashlog/:clusterName",
        element: <ProtectedRoute>{withSuspense(<Crashlog />)}</ProtectedRoute>
    },
    {
        path: "/query_database",
        element: <ProtectedRoute>{withSuspense(<DbQueryRunner />)}</ProtectedRoute>
    },
    {
        path: "/verify/:token",
        element: <PublicRoute>{withSuspense(<VerifyEmail />)}</PublicRoute>
    },
    {
        path: "/auth/callback",
        element: <PublicRoute>{withSuspense(<AuthCallback />)}</PublicRoute>
    },
    {
        path: "/notifications",
        element: <ProtectedRoute>{withSuspense(<Notifications />)}</ProtectedRoute>
    },
    {
        path: "/notifications/thresholds",
        element: <ProtectedRoute>{withSuspense(<PerformanceThresholds />)}</ProtectedRoute>
    },
    {
        path: "/performance_alerts",
        element: <ProtectedRoute>{withSuspense(<PerformanceAlerts />)}</ProtectedRoute>
    },
    {
        path: "/notification_settings",
        element: <ProtectedRoute>{withSuspense(<NotificationSettings />)}</ProtectedRoute>
    },
    {
        path: "/investigate/:clusterName/:notificationId",
        element: <ProtectedRoute>{withSuspense(<InvestigateNotification />)}</ProtectedRoute>
    },
    {
        path: "*",
        element: withSuspense(<NotFound />)
    }
]);

export default router;
